import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { photoSrc } from "@/lib/uploads";
import { ChevronLeft, Truck, FileText, Sparkles, AlertTriangle, Share2, Star, CreditCard, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusTimeline from "@/components/StatusTimeline";
import ShippingPanel from "@/components/ShippingPanel";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NEXT_STATUS = ["validated", "in_transit", "received", "diagnosed", "repairing", "repaired", "returned", "closed"];

const COVERAGE_BADGE = {
  covered: "bg-secondary/15 text-teal",
  partial: "bg-amber-100 text-amber-800",
  not_covered: "bg-destructive/10 text-destructive",
};
const DECISION_BADGE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-secondary/15 text-teal",
  rejected: "bg-destructive/10 text-destructive",
  more_info: "bg-primary/10 text-primary",
};

export default function ClaimDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [claim, setClaim] = useState(null);
  const [nextStatus, setNextStatus] = useState("");
  const [deductibleAmount, setDeductibleAmount] = useState("");
  const [deductibleReason, setDeductibleReason] = useState("");
  const [payingDeductible, setPayingDeductible] = useState(false);
  const [decisionNote, setDecisionNote] = useState("");
  const [decisionSaving, setDecisionSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/claims/${id}`);
    setClaim(data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const advance = async () => {
    if (!nextStatus) return;
    await api.post(`/claims/${id}/status`, { status: nextStatus });
    toast.success(`Marked as ${nextStatus}`);
    setNextStatus("");
    load();
  };

  const submitDecision = async (decision) => {
    setDecisionSaving(true);
    try {
      await api.post(`/claims/${claim.id}/decision`, { decision, note: decisionNote || null });
      const labels = { approved: "approved for repair", rejected: "rejected", more_info: "sent back for more info" };
      toast.success(`Claim ${labels[decision]}. Customer emailed.`);
      setDecisionNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save decision");
    } finally {
      setDecisionSaving(false);
    }
  };

  if (!claim) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const canManage = ["support", "admin", "logistics"].includes(user?.role);
  const sev = claim.ai_severity;
  const sevColor = sev === "severe" ? "bg-destructive/10 text-destructive" : sev === "moderate" ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary";
  const publicUrl = `${window.location.origin}/claim/${claim.share_token}`;
  const canRateNow = ["returned", "closed", "repaired"].includes(claim.status);
  const alreadyRated = !!claim.feedback_rating;

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Public link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const submitFeedback = async (rating) => {
    try {
      const { data } = await api.post(`/claims/${claim.id}/feedback`, { rating, note: "" });
      if (data?.review_url) {
        toast.success("Thanks for the great rating! Would you share it publicly?");
        window.open(data.review_url, "_blank", "noopener,noreferrer");
      } else if (data?.ticket_created) {
        toast.warning("Sorry we let you down. A support agent will reach out within 12 hours.");
      } else {
        toast.success("Thanks for your feedback!");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    }
  };

  const createDeductible = async () => {
    const amt = parseFloat(deductibleAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/claims/${claim.id}/deductible/create`, { amount_gbp: amt, reason: deductibleReason || null });
      toast.success(`Excess fee £${amt.toFixed(2)} sent to customer`);
      setDeductibleAmount(""); setDeductibleReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set excess");
    }
  };

  const payDeductible = async () => {
    setPayingDeductible(true);
    try {
      const { data } = await api.post(`/claims/${claim.id}/deductible/checkout`);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open payment");
      setPayingDeductible(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <Link to="/app/claims" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="w-4 h-4" /> Back to claims
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline text-secondary">Case</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight">{claim.case_number}</h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] overline px-2 py-1 rounded-full bg-primary text-primary-foreground">{claim.status.replace("_"," ")}</span>
            {sev && <span className={`text-[10px] overline px-2 py-1 rounded-full ${sevColor}`}>{sev} damage</span>}
            <span className="text-[10px] overline px-2 py-1 rounded-full bg-muted text-muted-foreground capitalize">{claim.issue_category}</span>
            {claim.coverage_status && (
              <span className={`text-[10px] overline px-2 py-1 rounded-full ${COVERAGE_BADGE[claim.coverage_status] || "bg-muted"}`} data-testid="claim-coverage-badge">
                {claim.coverage_status === "not_covered" ? "not covered" : claim.coverage_status}
              </span>
            )}
            {claim.admin_decision && (
              <span className={`text-[10px] overline px-2 py-1 rounded-full ${DECISION_BADGE[claim.admin_decision] || "bg-muted"}`} data-testid="claim-decision-badge">
                {claim.admin_decision === "more_info" ? "info requested" : claim.admin_decision}
              </span>
            )}
            {claim.counts_toward_quota === false && (
              <span className="text-[10px] overline px-2 py-1 rounded-full bg-muted text-muted-foreground">no quota impact</span>
            )}
            {claim.fraud_flag && <span className="text-[10px] overline px-2 py-1 rounded-full bg-destructive/10 text-destructive"><AlertTriangle className="w-3 h-3 inline mr-1" /> Fraud flag</span>}
          </div>
        </div>
        <Button variant="outline" onClick={copyShare} data-testid="claim-share-btn" className="hidden sm:inline-flex">
          <Share2 className="w-4 h-4 mr-2" /> Share status
        </Button>
      </div>

      {canManage && claim.admin_decision === "pending" && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-6" data-testid="admin-decision-panel">
          <div className="overline text-amber-800">Pending admin decision</div>
          <div className="mt-1 font-display text-xl text-amber-900">Review this claim</div>
          <p className="mt-2 text-sm text-amber-800">
            Coverage assessment: <b className="capitalize">{claim.coverage_status || "unknown"}</b>
            {claim.counts_toward_quota === false ? " · does not count toward yearly quota" : " · counts toward yearly quota"}.
            Decide whether to accept for repair.
          </p>
          <div className="mt-4">
            <Input
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              placeholder="Optional note to the customer (recommended for rejections)"
              data-testid="admin-decision-note"
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={() => submitDecision("approved")}
              disabled={decisionSaving}
              className="bg-teal text-white hover:bg-teal/90"
              data-testid="admin-decision-approve"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Accept for repair
            </Button>
            <Button
              onClick={() => submitDecision("rejected")}
              disabled={decisionSaving}
              variant="destructive"
              data-testid="admin-decision-reject"
            >
              <AlertTriangle className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button
              onClick={() => submitDecision("more_info")}
              disabled={decisionSaving}
              variant="outline"
              data-testid="admin-decision-more-info"
            >
              Request more info
            </Button>
          </div>
        </div>
      )}

      {canRateNow && (
        <div className="bg-secondary/10 border border-secondary/30 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="overline text-secondary">Post-repair feedback</div>
            <div className="mt-1 font-display text-lg">{alreadyRated ? `You rated this ${claim.feedback_rating}/5. Thank you!` : "How was your repair experience?"}</div>
          </div>
          {!alreadyRated && (
            <div className="flex items-center gap-1" data-testid="feedback-stars">
              {[1,2,3,4,5].map(r => (
                <button
                  key={r}
                  onClick={() => submitFeedback(r)}
                  className="text-secondary hover:scale-110 transition-transform"
                  aria-label={`Rate ${r}`}
                  data-testid={`feedback-star-${r}`}
                >
                  <Star className="w-6 h-6 fill-current" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: timeline */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="overline text-secondary">Repair timeline</div>
            <div className="mt-6">
              <StatusTimeline status={claim.status} stage={claim.mail_in_stage} events={claim.timeline} stuck={claim.stuck_flag} />
            </div>

            {canManage && claim.status !== "closed" && (
              <div className="mt-6 pt-6 border-t border-border flex items-center gap-2">
                <Select value={nextStatus} onValueChange={setNextStatus}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Advance to…" /></SelectTrigger>
                  <SelectContent>
                    {NEXT_STATUS.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={advance} disabled={!nextStatus}>Update status</Button>
              </div>
            )}
          </div>

          {claim.photos?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="overline text-secondary">Photos</div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                {claim.photos.map((p, i) => <img key={`${p.slice(0, 64)}-${i}`} src={photoSrc(p)} alt="" className="w-full h-40 object-cover rounded-lg border border-border" />)}
              </div>
            </div>
          )}
        </div>

        {/* Right: AI assessment + logistics */}
        <div className="space-y-6">
          <div className="bg-primary text-primary-foreground rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-40" />
            <div className="relative">
              <div className="overline text-secondary flex items-center gap-2"><Sparkles className="w-3 h-3" /> AI assessment</div>
              <div className="mt-3 text-3xl font-display capitalize">{claim.ai_severity || "—"}</div>
              <div className="mt-1 text-sm text-primary-foreground/80">{claim.ai_summary || "Awaiting AI analysis"}</div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="overline text-secondary">Estimate</div>
                  <div className="mt-1 font-display text-xl">£{claim.ai_estimate_gbp?.toFixed(2) || "—"}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="overline text-secondary">Confidence</div>
                  <div className="mt-1 font-display text-xl">{claim.ai_confidence ? Math.round(claim.ai_confidence * 100) + "%" : "—"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="overline text-secondary">Logistics (legacy)</div>
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <div className="text-muted-foreground text-xs">Courier</div>
                <div className="font-medium">{claim.courier || "—"}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Tracking</div>
                <div className="font-mono text-xs">{claim.tracking_number || "—"}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Shipping label</div>
                {claim.shipping_label ? (
                  <a href={claim.shipping_label} target="_blank" rel="noreferrer" className="text-primary underline text-xs">Download label</a>
                ) : "—"}
              </div>
            </div>
          </div>

          <ShippingPanel claim={claim} canManage={canManage} onRefresh={load} />

          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="overline text-secondary">Description</div>
            <div className="mt-3 text-sm text-foreground/80">{claim.description}</div>
          </div>

          {/* Deductible / Excess fee — Phase A */}
          {(claim.deductible_gbp || canManage) && (
            <div
              data-testid="deductible-card"
              className={`rounded-2xl p-6 border ${
                claim.deductible_paid ? "border-primary/40 bg-primary/5" : claim.deductible_gbp ? "border-secondary/40 bg-secondary/5" : "border-border bg-card"
              }`}
            >
              <div className="overline text-secondary flex items-center gap-2">
                <CreditCard className="w-3 h-3" /> Excess fee
              </div>

              {claim.deductible_paid && (
                <div className="mt-3 flex items-center gap-2 text-primary" data-testid="deductible-paid-badge">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-medium">Paid — £{claim.deductible_gbp?.toFixed(2)}</span>
                </div>
              )}

              {!claim.deductible_paid && claim.deductible_gbp && (
                <>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-3xl font-display font-semibold">£{claim.deductible_gbp.toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground">outstanding</span>
                  </div>
                  {claim.deductible_reason && (
                    <div className="mt-1 text-xs text-muted-foreground">{claim.deductible_reason}</div>
                  )}
                  {claim.user_id === user?.id && (
                    <Button
                      onClick={payDeductible}
                      disabled={payingDeductible}
                      className="mt-4 w-full bg-secondary text-teal hover:bg-secondary/90"
                      data-testid="pay-deductible-btn"
                    >
                      {payingDeductible ? "Opening…" : `Pay £${claim.deductible_gbp.toFixed(2)}`}
                    </Button>
                  )}
                </>
              )}

              {canManage && !claim.deductible_gbp && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-muted-foreground">
                    Charge an excess fee (e.g. water damage admin fee, theft excess) to the customer via Stripe.
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Amount £"
                    value={deductibleAmount}
                    onChange={(e) => setDeductibleAmount(e.target.value)}
                    data-testid="deductible-amount-input"
                  />
                  <Input
                    placeholder="Reason (optional)"
                    value={deductibleReason}
                    onChange={(e) => setDeductibleReason(e.target.value)}
                    data-testid="deductible-reason-input"
                  />
                  <Button
                    onClick={createDeductible}
                    className="w-full"
                    data-testid="create-deductible-btn"
                  >
                    Send excess to customer
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
